import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/States";

export default function Login() {
  const { signIn, brand } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.mfa_required) { setChallenge(data.challenge); return; }
      signIn(data);
    } catch (err) {
      setError(errMsg(err));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const { data } = await api.post("/auth/mfa/verify", { challenge, code: code.trim() }); signIn(data); }
    catch (err) { setError(errMsg(err)); if (err?.response?.status === 401 && /expired/i.test(errMsg(err))) setChallenge(null); }
    finally { setBusy(false); }
  };

  if (challenge) {
    return (
      <AuthLayout eyebrow="One more step" title="Enter your code">
        <form onSubmit={verify} className="space-y-5" data-testid="mfa-form">
          <p className="text-sm text-muted-foreground">Open your authenticator app and enter the six-digit code, or use one of your backup codes.</p>
          <div className="space-y-2"><Label htmlFor="mfa">Code</Label><Input id="mfa" autoFocus autoComplete="one-time-code" inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value)} className="font-mono text-lg tracking-widest" data-testid="mfa-login-code" /></div>
          {error && <p role="alert" className="text-sm text-destructive" data-testid="login-error">{error}</p>}
          <Button type="submit" className="w-full rounded-full" disabled={busy || code.trim().length < 6} data-testid="mfa-login-submit">{busy && <Spinner />} Continue</Button>
          <button type="button" className="text-xs text-muted-foreground underline-offset-4 hover:underline" onClick={() => { setChallenge(null); setCode(""); setError(""); }} data-testid="mfa-login-back">Back to password</button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow="Welcome back" title={brand.platform ? "Sign in" : `Sign in to ${brand.name}`}>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <div className="flex justify-between"><Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-muted-foreground underline-offset-4 hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
          <Input id="password" type="password" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
        </div>
        {error && <p role="alert" className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="login-submit-button">{busy && <Spinner />} Sign in</Button>
        <p className="text-xs text-muted-foreground">
          {brand.platform ? (brand.private_beta ? <span data-testid="login-beta-note">E.L.M is in private beta — new companies join by invitation.</span> : <>New company? <Link to="/signup" className="underline underline-offset-4" data-testid="login-signup-link">Start a free trial</Link>.</>) : `Team and client accounts are created from an invite link sent by ${brand.name}.`}
        </p>
      </form>
    </AuthLayout>
  );
}
